import { getRoute, RouteKey } from '@aboutyou/legacy-shared/src/localizedUrls'
import classnames from 'classnames'
import { strongifyMatchingText } from 'helpers/text'
import { appendParameters } from 'helpers/url'
import { inject } from 'mobx-react'
import React from 'react'
import { IStoreProvider } from 'store'
import UserStore from 'store/features/user'
import styled from 'styled-components'
import { primaryColor } from 'uiComponents/constants/colors'
import { fsXS, fwBold } from 'uiComponents/constants/typography'
import * as styles from './search-bar-styles.module.scss'

type Suggestions = {
  products?: string[]
  brands?: string[]
  categories?: string[]
}

type SuggestionsTitles = {
  productsTitle: string
  brandsTitle: string
  categoriesTitle: string
}

interface IProps {
  isOpen?: boolean
  searchQuery?: string
  lastSearches?: string[]
  suggestionTitles?: SuggestionsTitles // injected
  translatedLastSearchesTitle?: string // injected
  searchEndpoint?: string // injected
  userStore?: UserStore // injected
  suggestions?: Suggestions
  onElementClick: any
  isNotFoundStyle?: boolean
}

class FlyoutImpl extends React.Component<IProps, any> {
  getTermUrl = (term: string) => {
    const { searchEndpoint, userStore } = this.props
    return appendParameters(searchEndpoint!, {
      term,
      source: 'suggest',
      gender: userStore.gender,
    })
  }

  handleTermClick = (element: string) => {
    return (e: React.MouseEvent<HTMLAnchorElement>): void => {
      e.preventDefault()

      this.props.onElementClick(element)
      window.location.href = this.getTermUrl(element)
    }
  }

  renderCategoryElements = (terms: string[], isLastSection: boolean) => {
    return terms.map((term, index) => {
      const isLastElement = index === terms.length - 1
      const listElementClassnames = classnames({
        [styles.flyoutSectionListElement]: true,
        [styles.flyoutLastElement]: isLastElement && isLastSection,
      })

      return (
        <li key={index} className={listElementClassnames}>
          <TextElement
            href={this.getTermUrl(term)}
            onClick={this.handleTermClick(term)}
          >
            <TextWrapper $notFound={!!this.props.isNotFoundStyle}>
              {strongifyMatchingText(term, this.props.searchQuery)}
            </TextWrapper>
          </TextElement>
        </li>
      )
    })
  }

  renderSection = (
    elements: string[],
    title: string,
    isLastSection: boolean,
  ) => {
    return (
      <div key={title}>
        <Title>{title}</Title>
        <ul>{this.renderCategoryElements(elements, isLastSection)}</ul>
      </div>
    )
  }

  renderSuggestions = () => {
    const { suggestions, suggestionTitles } = this.props

    if (suggestions && suggestionTitles) {
      const { productsTitle, brandsTitle, categoriesTitle } = suggestionTitles
      const { products, brands, categories } = suggestions

      return (
        <>
          {products &&
            products.length > 0 &&
            this.renderSection(products, productsTitle, false)}
          {brands &&
            brands.length > 0 &&
            this.renderSection(brands, brandsTitle, false)}
          {categories &&
            categories.length > 0 &&
            this.renderSection(categories, categoriesTitle, true)}
        </>
      )
    }

    return <></>
  }

  render() {
    const { lastSearches, translatedLastSearchesTitle } = this.props

    const classNames = classnames({
      [styles.searchFlyout]: true,
      [styles.isNotFound]: this.props.isNotFoundStyle,
      [styles.searchFlyoutActive]: this.props.isOpen,
    })

    return (
      <div className={classNames}>
        {lastSearches &&
          translatedLastSearchesTitle &&
          this.renderSection(lastSearches, translatedLastSearchesTitle, true)}

        {this.props.suggestions && this.renderSuggestions()}
      </div>
    )
  }
}

const Flyout = inject((store: IStoreProvider) => {
  const t = store.t
  return {
    suggestionTitles: {
      productsTitle: t('Produkte'),
      brandsTitle: t('Marken'),
      categoriesTitle: t('Kategorien'),
    },
    searchEndpoint: getRoute(RouteKey.Search, store.localeStore.locale),
    translatedLastSearchesTitle: t('Deine letzte Suche'),
    userStore: store.userStore,
  }
})(FlyoutImpl)

const Title = styled.strong`
  display: block;
  line-height: 16px;
  font-size: 13px;
  ${fwBold};
  ${primaryColor};
  padding: 21px 0 6px 9px;
  text-align: left;
`

const TextWrapper = styled.div<{ $notFound: boolean }>`
  color: ${primaryColor};
  display: inline-block;
  font-weight: $fw-light;
  font-size: ${fsXS}px;
  letter-spacing: 0.8px;
  margin: 0 10px 0 19px;
  max-width: 230px;
  overflow: hidden;
  padding: 3px 0;
  position: relative;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: auto;

  ${({ $notFound }) => $notFound && 'max-width: calc(100% - 30px);'}

  &::after {
    content: '';
    visibility: hidden;
    position: absolute;
    right: 0;
    width: 100%;
    height: 1px;
    bottom: 0;
    background-color: ${primaryColor};
  }

  // TODO change the strong markup in NoFilterResults Component for a class and do the same here
  // had to do this because of markup over class preference in css compiling
  & strong {
    display: inline-block;
    ${fwBold};
    font-size: ${fsXS}px;
  }
`

const TextElement = styled.a`
  line-height: 16px;
  text-decoration: none;
  display: block;
  position: relative;
  text-align: left;
  width: 100%;
  padding: 12px 0;
  &:hover {
    cursor: pointer;

    ${TextWrapper} {
      &::after {
        visibility: visible;
      }
    }
  }
`

export default Flyout
